import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {capitalize} from "lodash";
import axios from "axios";

// Libs
import {
    boxShadow,
    primaryColor,
    textColor,
    blueColor,
    greenColor,
    lightBlueColor
} from "../common/libs/theme-lib";
import {getTokenFromLocalStorage} from "../auth/auth";

// Components
import Chart from "../common/components/chart";

const AdminHomePage = (props) => {

    // i18n
    const {t} = useTranslation("adminHome");

    // Props
    const {} = props;

    // State
    const [adminHomeStats, setAdminHomeStats] = useState({});

    useEffect(async () => {
        const res = await adminHomeStatsRequest();
        setAdminHomeStats(res?.data?.payload || {});
    }, []);

    return (
        <>
            <div style={{paddingBottom: "15px"}}>
                <div style={{
                    color: primaryColor,
                    fontSize: 28,
                    fontWeight: "bold"
                }}>{t("title")}</div>
                <div style={{fontWeight: 100, fontSize: 18}}>
                    {t("description")}
                </div>
            </div>
            <div style={{
                backgroundColor: "white",
                boxShadow: boxShadow,
                flex: 1,
                color: textColor
            }} className="container-fluid">
                <div className="row">
                    <StatsWrapper
                        type={"clients"}
                        data={adminHomeStats.clients}
                    />
                    <StatsWrapper
                        type={"users"}
                        data={adminHomeStats.users}
                    />
                </div>
                <div className="row pb-3">
                    <div className="col-12 col-lg-7 p-0">
                        <SectionTitle
                            title={t("chart.title")}
                        />
                        <div style={{
                            height: 280,
                            display: "flex"
                        }}>
                            <Chart
                                id={"logins"}
                                config={{
                                    grid: {
                                        top: 20,
                                        bottom: 20,
                                        left: 100,
                                        right: 100
                                    },
                                    tooltip: {
                                        trigger: "axis",
                                        axisPointer: {
                                            type: "shadow"
                                        },
                                        formatter: params => {
                                            return `
                                    <div>
                                        <div>
                                            ${capitalize(moment(params[0].name, "YYYY-M").format("MMMM YYYY"))}
                                        </div>
                                        ${params.map((param, index) => {
                                                return `
                                                <div key={${index}} style="display:flex;">
                                                    <div style="flex:1; padding-right:10px;">
                                                        ${param.marker} ${param.seriesName}
                                                    </div>
                                                    <div style="font-weight:bold;">
                                                        ${param.value[param.seriesId] || 0}
                                                    </div>
                                                </div>
                                            `;
                                            }).join("")}
                                    </div>
                                `;
                                        }
                                    },
                                    xAxis: {
                                        type: "category",
                                        axisLabel: {
                                            formatter: value => capitalize(moment(value, "YYYY-M").format("MMM")).substr(0, 3)
                                        }
                                    },
                                    yAxis: [{
                                        type: "value",
                                        axisLine: {
                                            show:true
                                        },
                                        splitLine: {
                                            lineStyle: {
                                                type: "dashed"
                                            }
                                        }
                                    }],
                                    series: [{
                                        type: 'bar',
                                        id: "count",
                                        name: t("chart.series.logins"),
                                        itemStyle: {
                                            color: lightBlueColor
                                        }, label: {
                                            show: true,
                                            position: 'insideBottom',
                                            offset: [0, 1],
                                            color: 'white',
                                            backgroundColor: lightBlueColor,
                                            padding: 3
                                        }
                                    }]
                                }}
                                data={{
                                    dimensions: ["month", "count"],
                                    source: adminHomeStats?.logins?.chart || []
                                }}
                            />
                        </div>
                    </div>
                    <div className="col-12 col-lg-5 p-0" style={{}}>
                        <SectionTitle
                            title={t("lastLogins")}
                        />
                        <div style={{
                            marginRight: 15,
                            paddingLeft: 5,
                            maxHeight: 280,
                            overflow: "auto"
                        }}>
                            {adminHomeStats?.logins?.last.map(({name, date}, index) =>{
                                return (
                                    <div key={index} style={{
                                        display: "flex",
                                        padding: "5px 10px"
                                    }}>
                                        <div title={name} style={{
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            overflow: "hidden",
                                            flex: 1,
                                            maxWidth: 400
                                        }}>{name}</div>
                                        <div>{moment(date).format("DD/MM/YYYY kk:mm:ss")}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminHomePage;

const StatsWrapper = (props) => {

    // i18n
    const {t} = useTranslation("adminHome");

    const {type, data = {}} = props;
    const {total = 0, active = 0, inactive = 0, last = []} = data;
    
    return (
        <div className="col-12 col-lg-6 p-0">
            <div style={{
                display: "flex",
                flexWrap: "wrap",
                paddingBottom: 15
            }}>
                <SectionTitle
                    title={type === "clients" ? t("clients"): t("users")}
                />
                <div style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "space-around",
                    flexWrap: "wrap"
                }}>
                    <Kpi
                        value={total}
                        text={t("kpis.total")}
                        color={blueColor}
                    />
                    <Kpi
                        value={active}
                        text={t("kpis.active")}
                        color={greenColor}
                    />
                    <Kpi
                        value={inactive}
                        text={t("kpis.inactive")}
                        color={primaryColor}
                    />
                </div>
            </div>
            <div style={{paddingBottom: 30}}>
                <SectionTitle
                    title={type === "clients" ? t("lastClients") : t("lastUsers")}
                />
                <div style={{
                    paddingLeft: 90,
                    marginRight: 15,
                    maxHeight: 140,
                    overflow: "auto"
                }}>
                    {last.map(({name, createdAt}, index) =>{
                        return (
                            <div key={index} style={{
                                display: "flex",
                                padding: "5px 10px"
                            }}>
                                <div title={name} style={{
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    flex: 1,
                                    maxWidth: 400,
                                    overflow: "hidden"
                                }}>{name}</div>
                                <div style={{paddingLeft: 5}}>{moment(createdAt).format("DD/MM/YYYY")}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const Kpi = (props) => {
    const {value, text, color} = props;
  return (
      <div style={{
          color,
          padding: 15
      }}>
          <div style={{
              fontSize: 28,
              fontWeight: "bold"
          }}>{value}</div>
          <div style={{
              fontSize: 22
          }}>{text}</div>
      </div>
  )  ;
};

const SectionTitle = ({title}) => {
    return (
        <div style={{
            padding: 15,
            fontSize: 20,
            fontWeight: "bold"
        }}>
            {title}
        </div>
    );
};

const adminHomeStatsRequest = async () => {
    const token = getTokenFromLocalStorage();
    return await axios.get("/api/stats/admin-home", { headers: { 'Authorization': 'Bearer ' + token } });
};